import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import SimuladorVidaUtil from '../SimuladorVidaUtil';
import ToolCta from '../ToolCta';
import { fadeInUp } from '../../../lib/animations';

const TOOL_SLUG = 'simulador-vida-util';

/** Página /ferramentas/simulador-vida-util */
const VidaUtilPage: React.FC = () => (
  <>
    <Helmet>
      <title>Simulador de Vida Útil de Peças: Correia, Óleo e Freios – Gas2</title>
      <meta
        name="description"
        content="Descubra se a correia dentada, o óleo ou as pastilhas de freio do seu carro já venceram pela quilometragem ou pelo tempo. Evite reparos caros com manutenção preventiva."
      />
      <link rel="canonical" href={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Gas2" />
      <meta property="og:url" content={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:title" content="Simulador de Vida Útil de Peças do Carro – Gas2" />
      <meta
        property="og:description"
        content="Saiba se a correia dentada, óleo, pastilha ou fluido de freio do seu carro já estão na hora de trocar. Simule pela KM e pelo tempo."
      />
      <meta property="og:image" content="https://gas2.app/logo512.png" />
      <meta property="og:locale" content="pt_BR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Simulador de Vida Útil de Peças – Gas2" />
      <meta
        name="twitter:description"
        content="Correia dentada, óleo, pastilha: descubra se alguma peça do seu carro já venceu antes que ela vença você."
      />
      <meta name="twitter:image" content="https://gas2.app/logo512.png" />
    </Helmet>

    <Navbar solid />

    <main className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-10">

        {/* ── H1 + intro ── */}
        <motion.header
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          <span className="inline-block rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600">
            Manutenção Preventiva
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Simulador de Vida Útil: Sua Correia Dentada Ainda Aguenta?
          </h1>
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed">
            Informe a quilometragem atual do painel, quando foi feita a última troca
            (em km e em meses) e selecione a peça. O simulador compara com os limites
            recomendados pelos fabricantes e alerta se a troca está próxima — ou se
            já passou da hora. Porque descobrir que a correia arrebentou na beira da
            estrada custa muito mais do que qualquer revisão preventiva.
          </p>
        </motion.header>

        {/* ── Simulador ── */}
        <SimuladorVidaUtil />

        {/* ── Conteúdo SEO ── */}
        <motion.article
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="space-y-8"
        >
          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Por Que a Correia Dentada É a Peça Mais Crítica do Motor?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              A correia dentada sincroniza o giro do virabrequim com o comando de válvulas.
              Se ela arrebentar com o motor em movimento, as válvulas e os pistões colidem
              — causando o chamado{' '}
              <strong className="text-slate-200">"motor fundido"</strong>. O reparo custa
              entre R$ 6.000 e R$ 15.000, dependendo do carro. A troca preventiva, por
              outro lado, gira em torno de R$ 400 a R$ 900.
            </p>
            <p className="text-slate-400 leading-relaxed mt-3">
              A maioria dos fabricantes recomenda a troca entre{' '}
              <strong className="text-slate-200">60.000 e 100.000 km</strong>, ou a cada
              4–5 anos, o que ocorrer primeiro. Se você não sabe quando foi feita a última
              troca, considere fazê-la imediatamente — o risco não vale.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Com Quantos KM Trocar o Óleo do Motor?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              A resposta depende do tipo de óleo e do motor. Óleos minerais exigem troca
              a cada <strong className="text-slate-200">5.000 a 7.500 km</strong>. Óleos
              semissintéticos, entre 7.500 e 10.000 km. Óleos sintéticos de alta
              performance chegam a{' '}
              <strong className="text-slate-200">15.000 km ou mais</strong> — mas sempre
              respeitando o prazo máximo de 1 ano, independente da quilometragem.
            </p>
            <p className="text-slate-400 leading-relaxed mt-3">
              Um óleo degradado perde viscosidade e poder lubrificante, aumentando o
              desgaste interno do motor silenciosamente. Use o{' '}
              <a href="/" className="text-amber-400 hover:text-amber-300 underline">
                app Gas2
              </a>{' '}
              para cadastrar a última troca e receber alertas automáticos quando estiver
              próximo do limite.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Pastilha de Freio: Quando Trocar sem Esperar o Rangido?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              O rangido metálico ao frear é o sinal de que a pastilha já chegou ao fim —
              e neste ponto o disco de freio pode já estar sendo danificado. A troca
              preventiva deve acontecer quando a espessura da pastilha chegar a{' '}
              <strong className="text-slate-200">2–3 mm</strong>, o que geralmente ocorre
              entre 25.000 e 40.000 km, dependendo do estilo de direção e do uso em cidade
              versus estrada.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Perguntas Frequentes sobre Vida Útil de Peças
            </h2>
            <div className="space-y-4 mt-3">
              {[
                {
                  q: 'Como saber se a correia dentada já foi trocada no carro usado?',
                  a: 'Peça o histórico de revisões ao vendedor ou verifique se há uma etiqueta colada no capô com data e KM da última troca. Na ausência de comprovação, assuma que não foi feita e inclua no orçamento da compra.',
                },
                {
                  q: 'Posso trocar só as pastilhas sem trocar o disco?',
                  a: 'Sim, se o disco ainda estiver dentro da espessura mínima indicada pelo fabricante. Leve o carro a um mecânico de confiança para medir. Se o disco estiver com marcas profundas ou empenado, a troca em conjunto é obrigatória.',
                },
                {
                  q: 'O fluido de freio precisa ser trocado mesmo sem usar?',
                  a: 'Sim. O fluido de freio (DOT) é higroscópico — absorve umidade do ar com o tempo, o que reduz seu ponto de ebulição e pode causar falha nos freios em descidas longas. A troca é recomendada a cada 2 anos, independente da quilometragem.',
                },
              ].map(({ q, a }) => (
                <div key={q} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
                  <p className="font-semibold text-white text-sm">{q}</p>
                  <p className="text-slate-400 text-sm mt-1 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </motion.article>

        {/* ── CTA download ── */}
        <ToolCta toolSlug={TOOL_SLUG} />
      </div>
    </main>

    <Footer />
  </>
);

export default VidaUtilPage;



